import { useEffect } from "react";
import Navbar from "./components/Navbar/Navbar";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import LoginComponent from "./pages/Login/login";
import InsightsLayout from "./pages/Events/InsightsLayout";
import BuildingsPage from "./pages/Events/BuildingsPage";
import SensorsPage from "./pages/Events/SensorsPage";
import SensorDetailPage from "./pages/Events/SensorDetailPage";
import BuildingReportPage from "./pages/Events/BuildingReportPage";
import SensorReportPage from "./pages/Events/SensorReportPage";
import "./index.css";
import ScrollToTop from "./ScrollToTop";
import AuthedApiProvider from "./components/AuthedApiProvider";
import PersonalAccountNotice from "./components/PersonalAccountNotice";
import HouseholdGate from "./components/HouseholdGate";
import WelcomePage from "./pages/Welcome/WelcomePage";
import { invitationLoginOptions } from "./services/invitationParams";
import AdminLayout from "./pages/Admin/AdminLayout";
import PeoplePage from "./pages/Admin/PeoplePage";
import MemberDetailPage from "./pages/Admin/MemberDetailPage";
import PendingInviteePage from "./pages/Admin/PendingInviteePage";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { muiTheme } from "./theme/MuiTheme";
import Spinner from "./components/ui/Spinner";
import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";

function FullPageLoader() {
  return (
    <div className="spinner">
      <Spinner size={96} />
    </div>
  );
}

const ProtectedRoute = ({ component: Component }) => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) {
      // Carrying the invitation, if this page arrived with one. Without it an
      // invitee is bounced into an ordinary login, signs in successfully, and
      // never joins the organization -- the invitation stays unaccepted for ever
      // and nothing reports an error, because the sign-in did work. That is this
      // call site's own history, and the reason the parameters are read in one
      // place rather than remembered at each.
      loginWithRedirect(invitationLoginOptions());
    }
  }, [isAuthenticated, loginWithRedirect]);

  return isAuthenticated ? <Component /> : null;
};

const queryClient = new QueryClient();

function App() {
  const { isAuthenticated, isLoading, error } = useAuth0();
  if (isLoading) return <FullPageLoader />;
  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        Oops... {error.message}
      </div>
    );
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        {/* Inside QueryClientProvider and outside Router, so every query made
            anywhere in the tree carries the bearer token -- including the admin
            routes, which are not nested under InsightsLayout. */}
        <AuthedApiProvider>
        {/* Inside AuthedApiProvider, because it reads /me and that needs the
            bearer token; outside Router, because landing in the wrong tenant is
            not a property of any one route -- the sensors are wrong too, not
            just the People page. */}
        <PersonalAccountNotice>
        {/* Below the notice, which explains the one state it covers rather than
            redirecting out of it. The ordering no longer decides anything: this
            gate makes its own check that the viewer belongs to no organization,
            and the server refuses a household for an organization member however
            it is asked. Auth0's own account chooser now runs before either of
            them and is where the organization is actually picked. */}
        <HouseholdGate>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Router>
            <ScrollToTop />
            <Navbar />
            <Routes>
              <Route
                path="/"
                element={
                  isAuthenticated ? (
                    <ProtectedRoute component={InsightsLayout} />
                  ) : (
                    <LoginComponent />
                  )
                }
              >
                <Route index element={<BuildingsPage />} />
              </Route>
              <Route path="/login" element={<LoginComponent />} />
              {/* Where a purchase email lands. Unprotected, necessarily: whoever
                  follows it has no account yet, which is the entire point. It is also
                  the only route in this product that offers a SIGN-UP rather than a
                  sign-in. */}
              <Route path="/welcome" element={<WelcomePage />} />
              <Route
                path="/admin"
                element={<ProtectedRoute component={AdminLayout} />}
              >
                <Route index element={<PeoplePage />} />
                <Route path="people" element={<PeoplePage />} />
                {/* Before the :userId route, or an invitation id would be read
                    as a user id and the member page would look for somebody who
                    does not exist. */}
                <Route
                  path="people/invitation/:invitationId"
                  element={<PendingInviteePage />}
                />
                <Route path="people/:userId" element={<MemberDetailPage />} />
              </Route>
              <Route
                path="/insights"
                element={<ProtectedRoute component={InsightsLayout} />}
              >
                <Route index element={<BuildingsPage />} />
                <Route
                  path="properties/:buildingId"
                  element={<SensorsPage />}
                />
                <Route
                  path="properties/:buildingId/report"
                  element={<BuildingReportPage />}
                />
                <Route
                  path="properties/:buildingId/sensors/:sensorId"
                  element={<SensorDetailPage />}
                />
                <Route
                  path="properties/:buildingId/sensors/:sensorId/report"
                  element={<SensorReportPage />}
                />
              </Route>
            </Routes>
          </Router>
        </LocalizationProvider>
        </HouseholdGate>
        </PersonalAccountNotice>
        </AuthedApiProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
